import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload as UploadIcon,
  FileText,
  CheckCircle,
  XCircle,
  Loader2,
  X,
  Camera,
  Image,
} from 'lucide-react';
import { uploadFacturas } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import toast from 'react-hot-toast';

export default function Upload() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState({});
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const addFiles = useCallback((newFiles) => {
    setResults(null);
    setFiles((prev) => [
      ...prev,
      ...newFiles.map((f) => ({
        file: f,
        id: `${f.name}-${Date.now()}-${Math.random()}`,
        name: f.name,
        size: f.size,
        preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
      })),
    ]);
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    addFiles(acceptedFiles);
  }, [addFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.tif', '.heic', '.heif'],
    },
    multiple: true,
    noClick: false,
  });

  const handleCameraCapture = (e) => {
    const capturedFiles = Array.from(e.target.files || []);
    if (capturedFiles.length > 0) {
      addFiles(capturedFiles);
    }
    e.target.value = '';
  };

  const handleGallerySelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
    e.target.value = '';
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setResults(null);

    const initialProgress = {};
    files.forEach((f) => {
      initialProgress[f.id] = { status: 'uploading', percent: 0 };
    });
    setProgress(initialProgress);

    files.forEach((f) => {
      let pct = 0;
      const interval = setInterval(() => {
        pct += Math.random() * 30;
        if (pct >= 90) {
          clearInterval(interval);
          pct = 90;
          setProgress((prev) => ({
            ...prev,
            [f.id]: { status: 'processing', percent: 90 },
          }));
        } else {
          setProgress((prev) => ({
            ...prev,
            [f.id]: { status: 'uploading', percent: Math.min(pct, 90) },
          }));
        }
      }, 300);
    });

    try {
      const rawFiles = files.map((f) => f.file);
      const result = await uploadFacturas(rawFiles);

      const completeProgress = {};
      files.forEach((f) => {
        completeProgress[f.id] = { status: 'complete', percent: 100 };
      });
      setProgress(completeProgress);

      const resultItems = Array.isArray(result)
        ? result
        : result.resultados || result.results || [];
      setResults(resultItems);
      toast.success(
        `${resultItems.length} factura${resultItems.length !== 1 ? 's' : ''} procesada${resultItems.length !== 1 ? 's' : ''}`
      );
    } catch (err) {
      const errorProgress = {};
      files.forEach((f) => {
        errorProgress[f.id] = { status: 'error', percent: 0 };
      });
      setProgress(errorProgress);
      toast.error('Error subiendo archivos: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    files.forEach((f) => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setFiles([]);
    setResults(null);
    setProgress({});
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'uploading': return 'Subiendo';
      case 'processing': return 'Procesando OCR';
      case 'complete': return 'Completado';
      case 'error': return 'Error';
      default: return 'Pendiente';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin text-primary-600" />;
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Escanear Facturas</h1>

      {/* Mobile camera/gallery buttons */}
      <div className="grid grid-cols-2 gap-3 sm:hidden">
        <button
          onClick={() => cameraInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 p-6 bg-primary-50 border-2 border-primary-200 rounded-xl text-primary-700 active:bg-primary-100 transition-colors"
        >
          <Camera className="w-8 h-8" />
          <span className="text-sm font-medium">Hacer Foto</span>
        </button>
        <button
          onClick={() => galleryInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 p-6 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-700 active:bg-gray-100 transition-colors"
        >
          <Image className="w-8 h-8" />
          <span className="text-sm font-medium">Galeria / Archivo</span>
        </button>
        {/* Hidden inputs for camera and gallery */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCameraCapture}
          className="hidden"
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="application/pdf,image/*"
          multiple
          onChange={handleGallerySelect}
          className="hidden"
        />
      </div>

      {/* Desktop dropzone (hidden on mobile) */}
      <div
        {...getRootProps()}
        className={`hidden sm:block border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary-400 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <UploadIcon
          className={`w-12 h-12 mx-auto mb-4 ${
            isDragActive ? 'text-primary-500' : 'text-gray-400'
          }`}
        />
        {isDragActive ? (
          <p className="text-primary-600 font-medium">Suelta los archivos aqui...</p>
        ) : (
          <>
            <p className="text-gray-600 font-medium">
              Arrastra y suelta archivos aqui, o haz clic para seleccionar
            </p>
            <p className="text-sm text-gray-400 mt-2">
              PDF, PNG, JPG, TIFF - Se pueden subir varios archivos a la vez
            </p>
          </>
        )}
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="card !p-4 sm:!p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase">
              Archivos ({files.length})
            </h2>
            <div className="flex gap-2">
              <button onClick={handleReset} className="btn-secondary text-xs sm:text-sm py-1.5 px-3" disabled={uploading}>
                Limpiar
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || files.length === 0}
                className="btn-primary flex items-center gap-1.5 text-xs sm:text-sm py-1.5 px-3"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UploadIcon className="w-4 h-4" />
                )}
                {uploading ? 'Procesando...' : 'Subir y Procesar'}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {files.map((f) => {
              const p = progress[f.id];
              return (
                <div
                  key={f.id}
                  className="flex items-center gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg"
                >
                  {/* Thumbnail for images */}
                  {f.preview ? (
                    <img src={f.preview} alt="" className="w-10 h-10 object-cover rounded" />
                  ) : (
                    p ? getStatusIcon(p.status) : <FileText className="w-4 h-4 text-gray-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {f.name}
                      </span>
                      <span className="text-xs text-gray-400 ml-2 shrink-0">{formatSize(f.size)}</span>
                    </div>
                    {p && (
                      <div className="mt-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all duration-300 ${
                                p.status === 'error'
                                  ? 'bg-red-500'
                                  : p.status === 'complete'
                                    ? 'bg-green-500'
                                    : 'bg-primary-500'
                              }`}
                              style={{ width: `${p.percent}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {getStatusLabel(p.status)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {!uploading && (
                    <button
                      onClick={() => removeFile(f.id)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Results */}
      {results && results.length > 0 && (
        <div className="card !p-4 sm:!p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3 sm:mb-4">
            Resultados
          </h2>
          {/* Mobile: card layout */}
          <div className="sm:hidden space-y-3">
            {results.map((r, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {r.archivo || r.filename || '-'}
                  </span>
                  <StatusBadge status={r.estado || 'pendiente'} />
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{r.numero_factura || '-'}</span>
                  <span className="font-mono font-medium text-gray-900">
                    {formatCurrency(r.total)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop: table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Archivo</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">N Factura</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {results.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-700">{r.archivo || r.filename || '-'}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.estado || 'pendiente'} /></td>
                    <td className="px-3 py-2 text-sm">{r.numero_factura || '-'}</td>
                    <td className="px-3 py-2 text-sm font-mono text-right">{formatCurrency(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
